import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useDropzone } from "react-dropzone";
import Btn from "../components/common/Btn";
import Input from "../components/common/Input";
import { Spinner } from "../components/common/Spinner";
import { delContents, postContents, putContents, contentsList } from "../api/contents";
import { fileApiService } from "../api/apiService";
import { postFiles } from "../api/files";
import { useAlertStore } from "../store/alertStore";
import { CommonBoxTitle, CommmonBoxLayout, CommonFooter, CommonLayout, FlexColBetweenBox } from "../style/common";
import { theme } from "../theme";
import { dateHyphen } from "../utils/dateFormChange";
import config from "../config";
import { gnbConst } from "../constants/gnbConst";
import { useMemberStore } from "../store/memberStore";
import { useNavigatePage } from "../hooks/useNavigatePage";

interface BannerItem {
  id: string;
  file: File | null;
  previewUrl: string;
  fileIdx?: string;
  isNew: boolean;
  title: string;
  startDate: string;
  endDate: string;
  url: string;
}

export default function BannerManager() {
    const [bannerItems, setBannerItems] = useState<BannerItem[]>([]);
    const [bannerListData, setBannerListData] = useState<ListGetApiResponse>();
    const [isSaving, setIsSaving] = useState(false);
    const { setAlert } = useAlertStore();
    const { memberPermitLevel } = useMemberStore();
    const baseUrl = useMemo(() => config.front.url || "", []);
    const { onMovementPage } = useNavigatePage();

    const fetchBannerList = async () => {
        try {
            const params = {
                page_index: 1,
                page_size: 100,
            };
            const response = await contentsList(params, "banner");
            setBannerListData(response);

            const mapped = response.list
                .sort((a, b) => (a.order_val || 0) - (b.order_val || 0))
                .map((item, index) => {
                    const file = item.files?.[0];
                    const resolvedUrl = file?.file_url?.startsWith("http") ? file.file_url : `${baseUrl}${file?.file_url || ""}`;

                    return {
                        id: item.idx,
                        file: null,
                        previewUrl: resolvedUrl || "",
                        fileIdx: file?.idx,
                        isNew: false,
                        title: item.title || `배너 ${index + 1}`,
                        startDate: item.date_start ? dateHyphen(item.date_start, "YYYY-MM-DD") : "",
                        endDate: item.date_end ? dateHyphen(item.date_end, "YYYY-MM-DD") : "",
                        url: item.url || "",
                    } as BannerItem;
                });

            setBannerItems(mapped);
        } catch (error) {
            setAlert("배너 목록을 불러오는 중 오류가 발생했습니다.", "red");
        }
    };

    const onDrop = (acceptedFiles: File[]) => {
        const today = new Date().toISOString().slice(0, 10);

        setBannerItems((prev) => {
            const startingIndex = prev.length;
            const newItems = acceptedFiles.map((file, idx) => ({
                id: Math.random().toString(36).substr(2, 9),
                file,
                previewUrl: URL.createObjectURL(file),
                isNew: true,
                title: `배너 ${startingIndex + idx + 1}`,
                startDate: today,
                endDate: "",
                url: "",
            }));

            return [...prev, ...newItems];
        });
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/*": [],
        },
    });

    const handleTitleChange = (id: string, value: string) => {
        setBannerItems((prev) => prev.map((item) => (item.id === id ? { ...item, title: value } : item)));
    };

    const handleUrlChange = (id: string, value: string) => {
        setBannerItems((prev) => prev.map((item) => (item.id === id ? { ...item, url: value } : item)));
    };

    const handleDateChange = (id: string, field: "startDate" | "endDate", value: string) => {
        setBannerItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
        );
    };

    const handleFileChange = (id: string, file: File | null) => {
        if (!file) return;
        setBannerItems((prev) =>
            prev.map((item) =>
                item.id === id
                    ? {
                        ...item,
                        file,
                        previewUrl: URL.createObjectURL(file),
                        isNew: true,
                    }
                    : item
            )
        );
    };

    const handleDelete = (id: string) => {
        setBannerItems((prev) => prev.filter((item) => item.id !== id));
    };

    const uploadFilesIfNeeded = async (items: BannerItem[]) => {
        const targets = items.filter((item) => item.file);

        if (targets.length === 0) return [];

        const uploaded = await Promise.all(
            targets.map(async (item) => {
                if (!item.file) return null;

                const formData = new FormData();
                formData.append("file", item.file, "[PROXY]");
                formData.append("file_path", "banner");
                formData.append("file_type", "1");
                formData.append("permit_level", "0");

                const filesResponse = await fileApiService.post<any>("/files/post.php", formData);
                if (filesResponse.statusCode !== 200) throw new Error("파일 업로드 실패");

                const postFilesResponse = await postFiles(filesResponse.data);
                if (postFilesResponse.statusCode !== 200) throw new Error("메타데이터 저장 실패");

                const fileData = postFilesResponse.data;
                const resolvedUrl = fileData.file_url?.startsWith("http")
                    ? fileData.file_url
                    : `${baseUrl}${fileData.file_url}`;

                return {
                    id: item.id,
                    fileIdx: fileData.idx,
                    previewUrl: resolvedUrl,
                };
            })
        );

        return uploaded.filter(Boolean) as { id: string; fileIdx: string; previewUrl: string }[];
    };

    const handleSave = async () => {
        if (bannerItems.length === 0) {
            setAlert("등록된 배너가 없습니다.", "gray");
            return;
        }

        const invalidDate = bannerItems.find((item) => !item.startDate || !item.endDate);
        if (invalidDate) {
            setAlert("모든 배너의 시작/종료 일자를 입력해주세요.", "red");
            return;
        }

        const missingImage = bannerItems.find((item) => !item.fileIdx && !item.file);
        if (missingImage) {
            setAlert("모든 배너에 이미지를 등록해주세요.", "red");
            return;
        }

        setIsSaving(true);

        try {
            const originalItems: BannerItem[] =
                bannerListData?.list.map((item, index) => {
                    const file = item.files?.[0];
                    const resolvedUrl = file?.file_url?.startsWith("http") ? file.file_url : `${baseUrl}${file?.file_url || ""}`;

                    return {
                        id: item.idx,
                        file: null,
                        previewUrl: resolvedUrl || "",
                        fileIdx: file?.idx,
                        isNew: false,
                        title: item.title || `배너 ${index + 1}`,
                        startDate: item.date_start ? dateHyphen(item.date_start, "YYYY-MM-DD") : "",
                        endDate: item.date_end ? dateHyphen(item.date_end, "YYYY-MM-DD") : "",
                        url: item.url || "",
                    };
                }) || [];

            const itemsToDelete = originalItems.filter((item) => !bannerItems.some((current) => current.id === item.id));
            const deletePromises = itemsToDelete.map((item) => delContents({ idx: item.id }, "banner"));

            const uploadedFiles = await uploadFilesIfNeeded(bannerItems);

            const mergedItems = bannerItems.map((item) => {
                const uploaded = uploadedFiles.find((upload) => upload.id === item.id);

                if (!uploaded) return item;

                return {
                    ...item,
                    file: null,
                    fileIdx: uploaded.fileIdx,
                    previewUrl: uploaded.previewUrl,
                    isNew: false,
                };
            });

            const savePromises = mergedItems.map((item, index) => {
                const payload = {
                    title: item.title || `배너 ${index + 1}`,
                    date_start: item.startDate ? `${item.startDate} 00:00:00` : undefined,
                    date_end: item.endDate ? `${item.endDate} 23:59:59` : undefined,
                    order_val: index + 1,
                    file_idx: item.fileIdx ? [item.fileIdx] : undefined,
                    url: item.url || undefined,
                };

                const exists = originalItems.some((origin) => origin.id === item.id);

                if (exists) {
                    return putContents({ ...payload, idx: item.id }, "banner");
                }

                return postContents(payload, "banner");
            });

            await Promise.all([...deletePromises, ...savePromises].filter(Boolean));

            setAlert("배너 정보가 저장되었습니다.", "green");
            fetchBannerList();
        } catch (error) {
            console.error(error);
            setAlert("저장 중 오류가 발생했습니다. 다시 시도해주세요.", "red");
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
    if (gnbConst[0].level > memberPermitLevel) {
        alert("접근 권한이 없습니다.");
        onMovementPage(-1);
    }
    }, [memberPermitLevel]);

    useEffect(() => {
        fetchBannerList();
    }, []);

    return (
        <CommonLayout>
            <CommmonBoxLayout>
                <FlexColBetweenBox>
                    <ContentBox>
                        <CommonBoxTitle>배너 관리</CommonBoxTitle>

                        <Section>
                            <SectionTitle>배너 추가</SectionTitle>
                            <DropzoneBox {...getRootProps()}>
                                <input {...getInputProps()} />
                                <p>{isDragActive ? "이미지를 여기에 놓아주세요." : "이미지를 드래그하거나 클릭하여 추가하세요."}</p>
                                <small>여러 장을 한 번에 업로드할 수 있습니다. (jpg, png 등)</small>
                            </DropzoneBox>
                        </Section>

                        <Section>
                            <SectionTitle>배너 리스트</SectionTitle>
                            {bannerItems.length === 0 ? (
                                <EmptyMsg>등록된 배너가 없습니다.</EmptyMsg>
                            ) : (
                                <CardList>
                                    {bannerItems.map((item) => (
                                        <BannerCard key={item.id}>
                                            <RemoveButton type="button" onClick={() => handleDelete(item.id)}>
                                                삭제
                                            </RemoveButton>
                                            <PreviewArea>
                                                {item.previewUrl ? (
                                                    <PreviewImg src={item.previewUrl} alt="banner-preview" />
                                                ) : (
                                            <Placeholder>이미지를 업로드해주세요.</Placeholder>
                                            )}
                                                <HiddenFileInput
                                                    id={`banner-file-${item.id}`}
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => handleFileChange(item.id, e.target.files?.[0] || null)}
                                                />
                                                <UploadLabel htmlFor={`banner-file-${item.id}`}>이미지 변경</UploadLabel>
                                            </PreviewArea>

                                            <InfoArea>
                                                <FieldRow>
                                                    <Input
                                                        type="text"
                                                        name="title"
                                                        width="17.5rem"
                                                        height="2.5rem"
                                                        placeholder="배너명을 입력하세요"
                                                        value={item.title}
                                                        onChange={(e) => handleTitleChange(item.id, e.target.value)}
                                                    />
                                                </FieldRow>
                                                <FieldRow>
                                                    <FieldLabel>링크</FieldLabel>
                                                    <Input
                                                        type="text"
                                                        name="url"
                                                        width="20rem"
                                                        height="2.5rem"
                                                        placeholder="연결할 URL을 입력하세요 (예: https://...)"
                                                        value={item.url}
                                                        onChange={(e) => handleUrlChange(item.id, e.target.value)}
                                                    />
                                                </FieldRow>
                                                <FieldRow>
                                                    <FieldLabel>시작일</FieldLabel>
                                                    <Input
                                                        type="date"
                                                        name="startDate"
                                                        width="13rem"
                                                        height="2.5rem"
                                                        value={item.startDate}
                                                        onChange={(e) => handleDateChange(item.id, "startDate", e.target.value)}
                                                    />
                                                </FieldRow>
                                                <FieldRow>
                                                    <FieldLabel>종료일</FieldLabel>
                                                    <Input
                                                        type="date"
                                                        name="endDate"
                                                        width="13rem"
                                                        height="2.5rem"
                                                        value={item.endDate}
                                                        onChange={(e) => handleDateChange(item.id, "endDate", e.target.value)}
                                                    />
                                                </FieldRow>
                                            </InfoArea>
                                        </BannerCard>
                                    ))}
                                </CardList>
                            )}
                        </Section>
                    </ContentBox>

                    <CommonFooter>
                        <div></div>
                        <Btn type="button" title="저장" color="primary" size="lg" onClick={handleSave} />
                    </CommonFooter>
                </FlexColBetweenBox>
            </CommmonBoxLayout>

            {isSaving && <Spinner />}
        </CommonLayout>
    );
}

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow-y: auto;
  padding-right: 1rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
`;

const DropzoneBox = styled.div`
  border: 2px dashed #ccc;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  background-color: #f9f9f9;
  transition: all 0.2s;

  &:hover {
    border-color: ${theme.color.primary};
    background-color: #f0f4ff;
  }

  p {
    color: #666;
    margin-bottom: 0.25rem;
  }

  small {
    color: #999;
  }
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BannerCard = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 3rem;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 0.75rem;
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewArea = styled.div`
  position: relative;
  width: 100%;
  max-width: 240px;
  min-height: 150px;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid #f0f0f0;
  background-color: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Placeholder = styled.span`
  color: #999;
  font-size: 0.95rem;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadLabel = styled.label`
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  right: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.375rem;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;
  cursor: pointer;
  font-size: 0.875rem;
  transition: background-color 0.2s;

  &:hover {
  background-color: rgba(0, 0, 0, 0.75);
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  padding: 0.35rem 0.75rem;
  z-index: 1;
  border: none;
  border-radius: 0.375rem;
  background-color: #ffebe8;
  color: #d32f2f;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.875rem;
  transition: background-color 0.2s, transform 0.2s;

  &:hover {
    background-color: #ffcdd2;
    transform: translateY(-1px);
  }
`;

const InfoArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  justify-content: center;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FieldLabel = styled.div`
  width: 3.5rem;
  font-weight: 600;
  color: #555;
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 2rem;
  color: #999;
  background-color: #f9f9f9;
  border-radius: 0.375rem;
`;
